// ============================================================
// DefenderModsCard - the defender's named custom modifiers
// ============================================================
// Each modifier's name and value edit in place; + Add opens a row that
// commits on Enter or Add (a zero or nameless modifier is refused) and
// closes on Escape or ×.

import { useState, type KeyboardEvent } from 'react';
import type { DefenderCustomMod } from '../types';
import NumberInput from './NumberInput';
import { activatable } from '../utils/keyActivation';
import clsx from '../utils/clsx';
import common from '../styles/common.module.css';
import styles from '../styles/TargetDefenderTab.module.css';

interface Props {
  mods: DefenderCustomMod[];
  onAdd: (mod: DefenderCustomMod) => void;
  onEdit: (id: string, updates: Partial<Pick<DefenderCustomMod, 'name' | 'value'>>) => void;
  onRemove: (id: string) => void;
  locked: boolean;
}

export function DefenderModsCard({ mods, onAdd, onEdit, onRemove, locked }: Props) {
  const [adding, setAdding] = useState(false);
  // The drafts outlive the row: Escape closes it and keeps them, × and a
  // commit clear them.
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const clear = () => {
    setAdding(false);
    setName('');
    setValue('');
  };
  const draft = { name, setName, value, setValue };
  return (
    <div className={styles.card} data-testid="defender-mods">
      <div className={styles.modsHeader}>
        <span className={clsx(styles.cardTitle, styles.cardTitleInline)}>CUSTOM MODIFIERS</span>
        {!locked && !adding && (
          <button className={clsx(common.btnSmall, styles.addModBtn)} onClick={() => setAdding(true)}>
            + Add
          </button>
        )}
      </div>
      {mods.length > 0 ? (
        <div className={styles.modList}>
          {mods.map((m) => (
            <ModRow key={m.id} mod={m} onEdit={onEdit} onRemove={onRemove} locked={locked} />
          ))}
        </div>
      ) : (
        <div className={styles.emptyNote}>No custom modifiers.</div>
      )}
      {adding && <AddModRow draft={draft} onAdd={onAdd} onEscape={() => setAdding(false)} onClear={clear} />}
    </div>
  );
}

function ModRow({ mod: m, onEdit, onRemove, locked }: { mod: DefenderCustomMod } & Omit<Props, 'mods' | 'onAdd'>) {
  return (
    <div className={styles.modRow}>
      <input
        className={clsx(common.input, styles.modName)}
        value={m.name}
        onChange={(e) => onEdit(m.id, { name: e.target.value })}
        disabled={locked}
        aria-label="Modifier name"
      />
      <NumberInput
        className={clsx(common.input, styles.modValue, m.value >= 0 ? styles.modValuePos : styles.modValueNeg)}
        allowNegative
        value={m.value}
        onChange={(value) => onEdit(m.id, { value })}
        disabled={locked}
        aria-label="Modifier value"
      />
      {!locked && (
        <span className={styles.removeX} {...activatable(() => onRemove(m.id))}>
          &times;
        </span>
      )}
    </div>
  );
}

interface Draft {
  name: string;
  setName: (name: string) => void;
  value: string;
  setValue: (value: string) => void;
}

interface AddModRowProps {
  draft: Draft;
  onAdd: Props['onAdd'];
  /** Close, keeping the drafts. */
  onEscape: () => void;
  /** Close and clear the drafts. */
  onClear: () => void;
}

/** The new-modifier row: Enter or Add commits, Escape closes, × discards. */
function AddModRow({ draft, onAdd, onEscape, onClear }: AddModRowProps) {
  const submit = () => {
    const numVal = parseInt(draft.value) || 0;
    if (!draft.name.trim() || numVal === 0) return;
    onAdd({ id: 'mod-' + Date.now(), name: draft.name.trim(), value: numVal });
    onClear();
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') submit();
    if (e.key !== 'Escape') return;
    // Stop here: the same key would otherwise close the panel the card sits in.
    e.stopPropagation();
    onEscape();
  };
  return (
    <div className={styles.addRow}>
      <input
        className={clsx(common.input, styles.addName)}
        placeholder="Name"
        value={draft.name}
        onChange={(e) => draft.setName(e.target.value)}
        onKeyDown={onKeyDown}
        // eslint-disable-next-line jsx-a11y/no-autofocus -- the add row appears on the user's own click
        autoFocus
      />
      <input
        className={clsx(common.input, styles.addValue)}
        placeholder="+/−"
        type="number"
        value={draft.value}
        onChange={(e) => draft.setValue(e.target.value)}
        onKeyDown={onKeyDown}
      />
      <button className={clsx(common.btnSmall, styles.confirmBtn)} onClick={submit}>
        Add
      </button>
      <span className={styles.cancelX} {...activatable(onClear)}>
        &times;
      </span>
    </div>
  );
}
